import { HttpClient, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
	ChangeTagsEnterprise,
	CountriesResponseMap,
	Country,
	CreateOrUpdateEnterprise,
	Enterprise,
	EnterpriseAddress,
	EnterpriseDetails,
	EnterpriseResponse,
	GeneralTag,
	LastEnterpriseModificationsResponse,
	LinkContactsToEnterprises,
	LinkEnterprisesToEnterprises,
	TaxValidator,
	TaxValidatorResponse,
	UpdateAddressTagsAssigned,
	UpdateTagsEnterprise,
	FilterEnterprise
} from '@interfaces'
import { BehaviorSubject, Observable, map } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class EnterpriseService {
	private http = inject(HttpClient)
	url = '/customers/enterprises'

	// Observable para conocer las empresas seleccionadas
	selectedEnterprisesSubject = new BehaviorSubject<Enterprise[]>([])
	$selectedEnterprises = this.selectedEnterprisesSubject.asObservable()
	// Observable para el cambio de usuario
	selectedUserSubject = new BehaviorSubject<number>(0)
	$selectedUser = this.selectedUserSubject.asObservable()
	// Observable para comunicar el multiselector con el popover de añadir etiquetas
	newTagsSubject = new BehaviorSubject<GeneralTag[]>([])
	$newTags = this.newTagsSubject.asObservable()
	// Observable para la gestión de etiquetas
	changeTagsSubject = new BehaviorSubject<ChangeTagsEnterprise>({})
	$changeTags = this.changeTagsSubject.asObservable()

	// Observable los detalles de la empresa
	enterpriseDetailsSubject = new BehaviorSubject<EnterpriseDetails | undefined>(undefined)
	$enterpriseDetails = this.enterpriseDetailsSubject.asObservable()

	// Observable para la edición de empresa en detalles
	isEditingEnterpriseSubject = new BehaviorSubject<boolean>(false)
	$isEditingEnterprise = this.isEditingEnterpriseSubject.asObservable()

	// Observable para saber si hay que actualizar el detalle de empresa
	updateEnterpriseDetailsSubject = new BehaviorSubject<boolean>(false)
	$updateEnterpriseDetails = this.updateEnterpriseDetailsSubject.asObservable()

	//Observable para saber si hay que actualizar la lista de empresas
	updateEnterprisesListSubject = new BehaviorSubject<boolean>(false)
	$updateEnterprisesList = this.updateEnterprisesListSubject.asObservable()

	// Llamada a la API para añadir una nueva dirección a una empresa
	addNewAddressToEnterprise(idEnterprise: number, address: EnterpriseAddress): Observable<EnterpriseDetails> {
		return this.http.put<EnterpriseDetails>(`${this.url}/${idEnterprise}/address`, address)
	}

	// Llamada al endpoint para asignar/eliminar etiquetas a unas empresas
	changeAssignedTags(data: UpdateTagsEnterprise): Observable<void> {
		return this.http.post<void>(`${this.url}/tag`, data)
	}

	// Llamada al endpoint para cambiar el usuario asignado a unas empresas
	changeAssignedUser(idUser: number, data: { id: number }[]): Observable<void> {
		return this.http.post<void>(`${this.url}/user?idUser=${idUser}`, data)
	}

	// Llamada al endpoint para para crear/actualizar una empresa
	createOrUpdateEnterprise(data: CreateOrUpdateEnterprise): Observable<EnterpriseDetails> {
		return this.http.post<EnterpriseDetails>(`${this.url}`, data)
	}

	// Llamada al endpoint para eliminar una dirección de una empresa
	deleteAddressToEnterprise(idEnterprise: number, idAddress: number) {
		return this.http.delete<void>(`${this.url}/${idEnterprise}/address/${idAddress}`)
	}

	// Llamada al endpoint para eliminar la asociación de una etiqueta con una dirección de una empresa
	deleteAssociatedTagToAddress(idEnterprise: number, idAssociatedAddress: number, idTag: number) {
		return this.http.delete<void>(`${this.url}/${idEnterprise}/address/${idAssociatedAddress}/tag/${idTag}`)
	}

	// Llamada al endpoint para eliminar una empresa
	deleteEnterprise(id: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${id}`)
	}

	// Llamada al endpoint para eliminar la asociación de una etiqueta con una empresa
	deleteEnterpriseAssociatedTag(idEnterprise: number, idTag: number) {
		return this.http.delete<void>(`${this.url}/${idEnterprise}/tag/${idTag}`)
	}

	// Llamada al endpoint para eliminar la asociación de un contacto con una empresa
	deleteEnterpriseLinkedContact(idEnterprise: number, idContact: number) {
		return this.http.delete<void>(`${this.url}/${idEnterprise}/associatedContact/${idContact}`)
	}

	// Llamada al endpoint para eliminar la asociación de una empresa con una empresa
	deleteEnterpriseLinkedEnterprise(idEnterprise: number, idAssociatedEnterprise: number) {
		return this.http.delete<void>(`${this.url}/${idEnterprise}/associatedEnterprise/${idAssociatedEnterprise}`)
	}

	// Llamada al endpoint para exportar la lista de empresas pasada como parámetro
	exportEnterprises(data: { ids?: number[] }): Observable<HttpResponse<Blob>> {
		return this.http.post(`${this.url}/export`, data, {
			observe: 'response',
			responseType: 'blob'
		})
	}

	// Llamada al endpoint para obtener el listado completo de empresas
	getAllEnterprises(): Observable<EnterpriseResponse> {
		return this.http.get<EnterpriseResponse>(`${this.url}`)
	}

	// Llamada al endpoint para obtener el listado de todos los países
	getCountries(): Observable<Country[]> {
		return this.http
			.get<CountriesResponseMap>(`/customers/countries`)
			.pipe(
				map(countries =>
					countries.content.sort(
						(a, b) => b.priorityOrder - a.priorityOrder || a.description.localeCompare(b.description)
					)
				)
			)
	}

	// Llamada al endpoint para obtener el listado paginado de las empresas
	getEnterprises(page = 0, size = 20, genericFilter = ''): Observable<EnterpriseResponse> {
		return this.http.get<EnterpriseResponse>(`${this.url}?page=${page}&size=${size}&genericFilter=${genericFilter}`)
	}

	// Llamada al endpoint para obtener los detalles de una empresa
	getEnterpriseDetails(id: number): Observable<EnterpriseDetails> {
		return this.http.get<EnterpriseDetails>(`${this.url}/${id}`)
	}

	// Recoge la lista de contactos filtrados
	getFilteredEnterprises(data: FilterEnterprise): Observable<EnterpriseResponse> {
		return this.http.post<EnterpriseResponse>(`${this.url}/search`, data)
	}

	// Llamada al endpoint para obtener todo el listado paginado de las últimas modificaciones de la empresa
	getLastModifications(idEnterprise: number, page = 0, size = 5): Observable<LastEnterpriseModificationsResponse> {
		return this.http.get<LastEnterpriseModificationsResponse>(
			`${this.url}/${idEnterprise}/lastModifications?page=${page}&size=${size}`
		)
	}

	// Desactiva el estado de edición
	resetIsEditingEnterprise() {
		this.isEditingEnterpriseSubject.next(false)
	}

	// Activa el estado de edición
	setIsEditingEnterprise() {
		this.isEditingEnterpriseSubject.next(true)
	}

	// Setea el listado de empresas
	setUpdateEnterprisessList() {
		this.updateEnterprisesListSubject.next(true)
	}

	// Llamada al endpoint para validar un código fiscal
	taxValidator(fiscalCode: TaxValidator): Observable<TaxValidatorResponse> {
		return this.http.post<TaxValidatorResponse>(`/tax-validator/validate`, fiscalCode)
	}

	// Llamada al endpoint para actualizar las etiquetas asociadas a una dirección de la empresa
	updateAddressAssignedTags(
		data: UpdateAddressTagsAssigned,
		idEnterprise: number,
		idAddress: number
	): Observable<void> {
		return this.http.post<void>(`${this.url}/${idEnterprise}/address/${idAddress}/tag`, data)
	}

	// Llamada al endpoint para actualizar los contactos asociados a una empresa
	updateLinkedContacts(data: LinkContactsToEnterprises, id: number): Observable<void> {
		return this.http.post<void>(`${this.url}/${id}/associatedContact`, data)
	}

	// Llamada al endpoint para actualizar las empresas asociadas a una empresa
	updateLinkedEnterprises(data: LinkEnterprisesToEnterprises, id: number): Observable<void> {
		return this.http.post<void>(`${this.url}/${id}/associatedEnterprise`, data)
	}
}
